import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/apiClient';

export default function ClinicSettings() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    cabinet_name: '',
    num_gkn: '',
    num_rcc: '',
    phone: '',
    place: '',
    logo_image: '',
    logo_image_mime: ''
  });

  useEffect(() => {
    const fetchClinicData = async () => {
      try {
        const data = await apiClient.get('/api/clinic');
        setFormData({
          cabinet_name: data.cabinet_name ?? '',
          num_gkn: data.num_gkn ?? '',
          num_rcc: data.num_rcc ?? '',
          phone: data.phone ?? '',
          place: data.place ?? '',
          logo_image: data.logo_image ?? '',
          logo_image_mime: data.logo_image_mime ?? ''
        });
      } catch (err) {
        console.error('Error fetching clinic data:', err);
        setError(t('settings.clinic.error.fetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result; // data URL
      setFormData(prev => ({
        ...prev,
        logo_image: result,
        logo_image_mime: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      console.log('Attempting to update clinic with data:', formData);

      const responseData = await apiClient.patch('/api/clinic', {
        ...formData,
        logo_image: formData.logo_image || null,
        logo_image_mime: formData.logo_image_mime || null
      });
      console.log('Setting form data with server response:', responseData);
      setFormData(responseData);
      setSuccess(true);
    } catch (err) {
      console.error('Error saving clinic settings:', {
        error: err,
        message: err.message,
        stack: err.stack,
        formData: formData
      });

      const errorMessage = err.message || t('settings.clinic.error.save');
      setError(`${t('settings.clinic.error.save')}: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          {t('settings.clinic.title')}
        </h2>
      </div>

      <div className="px-6 py-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Cabinet Name */}
            <div>
              <label htmlFor="cabinet_name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.cabinet_name')}
              </label>
              <input
                type="text"
                id="cabinet_name"
                name="cabinet_name"
                value={formData.cabinet_name}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            {/* GKN Number */}
            <div>
              <label htmlFor="num_gkn" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.num_GKN')}
              </label>
              <input
                type="text"
                id="num_gkn"
                name="num_gkn"
                value={formData.num_gkn}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            {/* RCC Number */}
            <div>
              <label htmlFor="num_rcc" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.num_RCC')}
              </label>
              <input
                type="text"
                id="num_rcc"
                name="num_rcc"
                value={formData.num_rcc}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            {/* Place */}
            <div>
              <label htmlFor="place" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.place', 'Lieu')}
              </label>
              <input
                type="text"
                id="place"
                name="place"
                value={formData.place}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Logo Upload */}
          <div className="mt-6 grid grid-cols-2 gap-4 items-start">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.clinic.logo', 'Logo')}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFile}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">{t('settings.clinic.logoHint', 'PNG/JPG')}</p>
            </div>
            <div className="flex items-center justify-center">
              {formData.logo_image ? (
                <img
                  src={formData.logo_image}
                  alt="logo preview"
                  className="max-h-24 max-w-full border rounded"
                />
              ) : (
                <div className="text-sm text-gray-500">{t('settings.clinic.noLogo', 'No image uploaded')}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
            >
              {saving ? t('common.saving') : t('settings.clinic.save')}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
              {t('settings.clinic.success')}
            </div>
          )}
        </form>
      </div>
    </div>
  );
} 